if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = (this.dataset.url !== productUrl) || this.querySelector('li[data-gang-option]') !== null;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects[data-primary]');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
        const variantSelectsAlt = html.querySelector('variant-selects[data-sticky]');
        if (variantSelectsAlt) {
          HTMLUpdateUtility.viewTransition(this.variantSelectorsAlt, variantSelectsAlt, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // Check if variant has custom images before updating media
          const updatedMediaGallery = html.querySelector('media-gallery');
          const hasVariantImages = updatedMediaGallery && (
            updatedMediaGallery.querySelector('[data-variant-images]') ||
            updatedMediaGallery.hasAttribute('data-variant-images') ||
            updatedMediaGallery.querySelector('[id^="GalleryViewer"]')?.hasAttribute('data-variant-images')
          );

          // Only update standard media if variant doesn't have custom images
          if (!hasVariantImages) {
            this.updateMedia(html, variant?.featured_media?.id);
          }

          // Replace the updateVariantElements call with direct updates
          this.updateVariantImage(html);
          this.updateVariantMediaGallery(html);
          this.updateVariantDescription(html);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Price-Alt');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            theme.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable);

        const selectors = ['price', 'Price-Alt', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      updateVariantImage(html) {
        const currentImageContainer = this.querySelector('.product__variant-image');
        const updatedImageContainer = html.querySelector('.product__variant-image');
        
        if (currentImageContainer && updatedImageContainer) {
          currentImageContainer.innerHTML = updatedImageContainer.innerHTML;
        }
      }

      updateVariantMediaGallery(html) {
        const currentMediaGallery = this.querySelector('media-gallery');
        const updatedMediaGallery = html.querySelector('media-gallery');
        
        if (currentMediaGallery && updatedMediaGallery) {
          // Check if the variant has custom images in metafield
          const variantImagesList = updatedMediaGallery.querySelector('[data-variant-images]');
          const updatedViewer = updatedMediaGallery.querySelector('[id^="GalleryViewer"]');
          const hasVariantImages = !!variantImagesList || 
            updatedMediaGallery.hasAttribute('data-variant-images') || 
            updatedViewer?.hasAttribute('data-variant-images');
          
          console.log('Variant images detected:', hasVariantImages);
          
          if (hasVariantImages) {
            console.log('Updating media gallery with variant images');
            
            const currentViewer = currentMediaGallery.querySelector('[id^="GalleryViewer"]');
            
            // Replace the viewer content
            if (currentViewer && updatedViewer) {
              currentViewer.innerHTML = updatedViewer.innerHTML;
              Array.from(updatedViewer.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                  currentViewer.setAttribute(attr.name, attr.value);
                }
              });
              console.log('Updated gallery viewer');
            }
            
            // Handle standard thumbnails (for thumbnail layout)
            const currentThumbnails = currentMediaGallery.querySelector('[id^="GalleryThumbnails"]');
            const updatedThumbnails = updatedMediaGallery.querySelector('[id^="GalleryThumbnails"]');
            if (currentThumbnails && updatedThumbnails) {
              currentThumbnails.innerHTML = updatedThumbnails.innerHTML;
              
              // Re-attach click event listeners to thumbnails
              currentThumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                mediaToSwitch.querySelector('button').addEventListener('click', 
                  currentMediaGallery.setActiveMedia.bind(currentMediaGallery, mediaToSwitch.dataset.target, false, true)
                );
              });
              console.log('Updated gallery thumbnails and re-attached event listeners');
            }
            
            // Handle thumbnail slider
            const thumbnailSlider = currentMediaGallery.querySelector('thumbnail-slider');
            const updatedThumbnailSlider = updatedMediaGallery.querySelector('thumbnail-slider');
            if (thumbnailSlider && updatedThumbnailSlider) {
              const currentSliderContent = thumbnailSlider.querySelector('[id^="Slider-Thumbnails"]');
              const updatedSliderContent = updatedThumbnailSlider.querySelector('[id^="Slider-Thumbnails"]');
              
              if (currentSliderContent && updatedSliderContent) {
                currentSliderContent.innerHTML = updatedSliderContent.innerHTML;
                
                // Re-attach click event listeners to thumbnail slider items
                currentSliderContent.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                  mediaToSwitch.querySelector('button').addEventListener('click', 
                    currentMediaGallery.setActiveMedia.bind(currentMediaGallery, mediaToSwitch.dataset.target, false, true)
                  );
                });
                
                thumbnailSlider.dispatchEvent(new CustomEvent('thumbnails:updated', { bubbles: true }));
                console.log('Updated thumbnail slider and re-attached event listeners');
              } else if (currentSliderContent && !updatedSliderContent) {
                // Generate thumbnails from variant images
                console.log('Generating thumbnails from variant images');
                const variantImages = updatedMediaGallery.querySelectorAll('[id^="Slider-Gallery"] [data-media-id]');
                
                if (variantImages.length > 0) {
                  currentSliderContent.innerHTML = '';
                  
                  variantImages.forEach((variantImage, index) => {
                    const mediaId = variantImage.dataset.mediaId;
                    const mediaSrc = variantImage.querySelector('img')?.src;
                    const mediaPosition = variantImage.dataset.mediaPosition || index + 1;
                    
                    if (mediaId && mediaSrc) {
                      const thumbnailItem = document.createElement('li');
                      thumbnailItem.id = `Slide-Thumbnails-${this.dataset.section}-${index}`;
                      thumbnailItem.className = `thumbnail-list__item slider__slide${index === 0 ? ' is-active' : ''} media-wrapper media-wrapper--small`;
                      thumbnailItem.setAttribute('data-target', mediaId);
                      thumbnailItem.setAttribute('data-media-position', mediaPosition);
                      
                      const button = document.createElement('button');
                      button.className = 'thumbnail global-media-settings';
                      button.setAttribute('aria-label', `Go to slide ${index + 1}`);
                      button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
                      
                      // Add click event listener
                      button.addEventListener('click', 
                        currentMediaGallery.setActiveMedia.bind(currentMediaGallery, mediaId, false, true)
                      );
                      
                      const img = document.createElement('img');
                      img.className = 'thumbnail__image';
                      img.src = mediaSrc.replace(/(\.[^.]+)$/, '_150x$1').replace('_1000x', '_150x');
                      img.alt = '';
                      img.loading = 'lazy';
                      img.width = 150;
                      img.height = 150;
                      
                      button.appendChild(img);
                      thumbnailItem.appendChild(button);
                      currentSliderContent.appendChild(thumbnailItem);
                    }
                  });
                  
                  console.log('Generated thumbnails with event listeners');
                }
                
                thumbnailSlider.dispatchEvent(new CustomEvent('thumbnails:updated', { bubbles: true }));
              }
            }
            
            // Handle stacked thumbnails (product-thumbnails custom element)
            const currentStackedThumbnails = currentMediaGallery.querySelector('product-thumbnails');
            const updatedStackedThumbnails = updatedMediaGallery.querySelector('product-thumbnails');
            if (currentStackedThumbnails && updatedStackedThumbnails) {
              // Replace the entire custom element to reinitialize it
              const parent = currentStackedThumbnails.parentNode;
              const newStackedThumbnails = updatedStackedThumbnails.cloneNode(true);
              parent.replaceChild(newStackedThumbnails, currentStackedThumbnails);
              console.log('Replaced product-thumbnails element');
            }
            
            // Update the gallery list (Slider-Gallery) with variant images
            const currentImagesList = currentMediaGallery.querySelector('[id^="Slider-Gallery"]');
            const updatedImagesList = updatedMediaGallery.querySelector('[id^="Slider-Gallery"]');
            if (currentImagesList && updatedImagesList) {
              currentImagesList.innerHTML = updatedImagesList.innerHTML;
              currentImagesList.setAttribute('data-variant-images', 'true');
              console.log('Updated gallery list with variant images');
            }
            
            // Set first media as active
            setTimeout(() => {
              const firstMedia = currentViewer?.querySelector('[data-media-id]');
              if (firstMedia && typeof currentMediaGallery.setActiveMedia === 'function') {
                currentMediaGallery.setActiveMedia(firstMedia.dataset.mediaId, false, false);
                console.log('Set first media as active:', firstMedia.dataset.mediaId);
              }
            }, 100);
            
            this.reinitializeGalleryFeatures();
          } else {
            // New variant has NO custom images -> restore default gallery
            console.log('Restoring default gallery because variant has no custom images');
            
            const currentViewer = currentMediaGallery.querySelector('[id^="GalleryViewer"]');
            const updatedViewer = updatedMediaGallery.querySelector('[id^="GalleryViewer"]');
            
            // Update the main gallery viewer
            if (currentViewer && updatedViewer) {
              currentViewer.innerHTML = updatedViewer.innerHTML;
              Array.from(updatedViewer.attributes).forEach(attr => {
                if (attr.name.startsWith('data-') || attr.name === 'class' || attr.name === 'id') {
                  currentViewer.setAttribute(attr.name, attr.value);
                }
              });
              console.log('Restored default gallery viewer');
            }
            
            // Update standard thumbnails with event listeners
            const currentThumbnails = currentMediaGallery.querySelector('[id^="GalleryThumbnails"]');
            const updatedThumbnails = updatedMediaGallery.querySelector('[id^="GalleryThumbnails"]');
            if (currentThumbnails && updatedThumbnails) {
              currentThumbnails.innerHTML = updatedThumbnails.innerHTML;
              
              // Re-attach click event listeners
              currentThumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                mediaToSwitch.querySelector('button').addEventListener('click', 
                  currentMediaGallery.setActiveMedia.bind(currentMediaGallery, mediaToSwitch.dataset.target, false, true)
                );
              });
              console.log('Restored default thumbnails with event listeners');
            }
            
            // Update thumbnail slider with event listeners
            const thumbnailSlider = currentMediaGallery.querySelector('thumbnail-slider');
            const updatedThumbnailSlider = updatedMediaGallery.querySelector('thumbnail-slider');
            if (thumbnailSlider && updatedThumbnailSlider) {
              const currentSliderContent = thumbnailSlider.querySelector('[id^="Slider-Thumbnails"]');
              const updatedSliderContent = updatedThumbnailSlider.querySelector('[id^="Slider-Thumbnails"]');
              
              if (currentSliderContent && updatedSliderContent) {
                currentSliderContent.innerHTML = updatedSliderContent.innerHTML;
                
                // Re-attach click event listeners to thumbnail slider items
                currentSliderContent.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                  mediaToSwitch.querySelector('button').addEventListener('click', 
                    currentMediaGallery.setActiveMedia.bind(currentMediaGallery, mediaToSwitch.dataset.target, false, true)
                  );
                });
                
                thumbnailSlider.dispatchEvent(new CustomEvent('thumbnails:updated', { bubbles: true }));
                console.log('Restored default thumbnail slider with event listeners');
              }
            }
            
            // Update stacked thumbnails (product-thumbnails custom element)
            const currentStackedThumbnails = currentMediaGallery.querySelector('product-thumbnails');
            const updatedStackedThumbnails = updatedMediaGallery.querySelector('product-thumbnails');
            if (currentStackedThumbnails && updatedStackedThumbnails) {
              // Replace the entire custom element to reinitialize it
              const parent = currentStackedThumbnails.parentNode;
              const newStackedThumbnails = updatedStackedThumbnails.cloneNode(true);
              parent.replaceChild(newStackedThumbnails, currentStackedThumbnails);
              console.log('Restored default product-thumbnails element');
            }
            
            // Remove variant-images flags
            const currentImagesList = currentMediaGallery.querySelector('[id^="Slider-Gallery"]');
            currentImagesList?.removeAttribute('data-variant-images');
            currentMediaGallery.removeAttribute('data-variant-images');
            console.log('Removed variant-images attributes');
            
            // Set first media as active
            setTimeout(() => {
              const firstMedia = currentMediaGallery.querySelector('[id^="GalleryViewer"] [data-media-id]');
              if (firstMedia && typeof currentMediaGallery.setActiveMedia === 'function') {
                currentMediaGallery.setActiveMedia(firstMedia.dataset.mediaId, false, false);
                console.log('Set first default media as active');
              }
            }, 100);
            
            this.reinitializeGalleryFeatures();
          }
        }
      }

      reinitializeGalleryFeatures() {
                  // The variant swap replaces the sliders' insides (innerHTML), so their
          // cached slider list and arrow buttons point at detached elements. Point
          // them at the new ones and re-attach their listeners.
          this.querySelectorAll("product-gallery, thumbnail-slider").forEach((el) => {
            el.slider = el.querySelector('[id^="Slider-"]');
            el.sliderItems = el.querySelectorAll('[id^="Slide-"]');
            el.prevButton = el.querySelector('button[name="previous"]');
            el.nextButton = el.querySelector('button[name="next"]');
            el.currentPageElement = el.querySelector(".slider-counter--current");
            el.pageTotalElement = el.querySelector(".slider-counter--total");
            if (el.slider && el.nextButton) el.init();
          });
        // Reinitialize any gallery-specific JavaScript features
        if (this.hasAttribute('data-zoom-on-hover')) {
          enableZoomOnHover(2);
        }
        
        // Reinitialize any slider functionality
        const viewer = this.querySelector('[id^="GalleryViewer"]');
        if (viewer && viewer.slider && typeof viewer.slider.resetPages === 'function') {
          viewer.slider.resetPages();
        } else if (viewer) {
          // Fallback: trigger a resize event to recalculate dimensions
          window.dispatchEvent(new Event('resize'));
        }
        
        // Reinitialize thumbnail slider if it exists
        const thumbnailSlider = this.querySelector('thumbnail-slider');
        if (thumbnailSlider) {
          // If it has a slider with resetPages method, use it
          if (thumbnailSlider.slider && typeof thumbnailSlider.slider.resetPages === 'function') {
            thumbnailSlider.slider.resetPages();
          }
          
          // Dispatch the custom event for reinitializing the thumbnails
          thumbnailSlider.dispatchEvent(new CustomEvent('thumbnails:updated', { bubbles: true }));
          console.log('Dispatched thumbnails:updated event to thumbnail slider');
        }

        // Reinitialize stacked thumbnails
        const productThumbnails = this.querySelector('product-thumbnails');
        if (productThumbnails && typeof productThumbnails.reinitialize === 'function') {
          productThumbnails.reinitialize();
          console.log('Called reinitialize on product-thumbnails');
        } else if (productThumbnails) {
          // Fallback if the reinitialize method isn't available
          productThumbnails.dispatchEvent(new CustomEvent('thumbnails:updated', { bubbles: true }));
          console.log('Dispatched thumbnails:updated event to product-thumbnails');
        }
        
        // Trigger any gallery initialization events
        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery) {
          mediaGallery.dispatchEvent(new CustomEvent('gallery:updated', { bubbles: true }));
          console.log('Dispatched gallery:updated event to media gallery');
        }
      }

      updateVariantDescription(html) {
        const currentDescription = this.querySelector('[data-variant-description]');
        const updatedDescription = html.querySelector('[data-variant-description]');
        
        if (currentDescription && updatedDescription) {
          currentDescription.innerHTML = updatedDescription.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        //this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            //this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects[data-primary]');
      }

      get variantSelectorsAlt() {
        return this.querySelector('variant-selects[data-sticky]');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
